import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { FormWizard } from "~/components/form-inputs";
import { ActionBadge } from "~/components/shared/action-badge";
import { IAction } from "~/components/shared/add-action-modal/types";
import {
  AvatarStack,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "~/components/ui";
import TheAduseiEditorViewer from "~/components/ui/adusei-editor/viewer";
import TableBadge from "~/components/shared/datatable/badge";
import { DEFAULT_API_PAYLOAD, InputTypes } from "~/lib/constants";
import { getInitials, getListsForOptions } from "~/lib/utils";
import { handleError } from "~/lib/error";
import { PostApiV1ActionsCommentApiArg } from "~/redux/api/openapi.generated";
import { restApi } from "~/redux/restApi";
import { useDispatch } from "~/redux/store";
import { commonActions } from "~/redux/slice/common";
import { useSelector } from "~/redux/store";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  action: IAction | null;
  /** Refetch the parent list when something changes inside the modal */
  onRefresh?: () => void;
}

const CommentSchema = {
  comment: "",
  attachments: null as any,
};

const formatCommentDate = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const timePart = format(date, "h:mm a");

    if (isToday) {
      return `today | ${timePart}`;
    } else if (isYesterday) {
      return `Yesterday | ${timePart}`;
    } else {
      return `${format(date, "MMMM dd, yyyy")} | ${timePart}`;
    }
  } catch {
    return "";
  }
};

export const ActionWorkspaceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  action,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const userId = useSelector(
    (state: any) => state.persistedReducer.auth.userId,
  );
  const [triggerRender, setTriggerRender] = useState(0);

  // ── Remote data ────────────────────────────────────────────────────────────
  const [loadAction, { data: singleResponse }] =
    restApi.useLazyGetSingleActionIdQuery();
  const [updateActionStatus] = restApi.useUpdateActionsStatusMutation();
  const [loadComments, { data: commentData, isLoading: isLoadingComments }] =
    restApi.useLazyGetCommentByActionIdQuery();
  const [saveComment, { isLoading: postingComment }] =
    restApi.useSaveCommentMutation();

  const single = singleResponse?.result ?? action;
  const comments = (commentData as any)?.result?.data ?? [];
  const actionStatuses = getListsForOptions(single?.dropdownStatuses as any[]);

  const isAssignedToMe = userId === single?.responsiblePerson?.id;
  const isNotCompleted =
    single?.status?.name !== "Closed" &&
    single?.status?.name !== "Ready for Review" &&
    single?.status?.name !== "Voided";

  const isCapa =
    (single as any)?.category === "CAPA" ||
    single?.type === "CorrectiveAction" ||
    single?.type === "CAPA";

  // ── Load on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && action?.id) {
      loadAction({ ...DEFAULT_API_PAYLOAD, id: action.id });
      loadComments({
        ...DEFAULT_API_PAYLOAD,
        actionId: action.id,
        pageSize: 50,
      });
    }
  }, [isOpen, action?.id, triggerRender, loadAction, loadComments]);

  // ── Comment form ───────────────────────────────────────────────────────────
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: CommentSchema,
  });

  const watchAttachments = watch("attachments");
  const [draftFiles, setDraftFiles] = useState<File[]>([]);

  useEffect(() => {
    if (watchAttachments) {
      const files = Array.isArray(watchAttachments)
        ? watchAttachments
        : Array.from(watchAttachments as FileList);
      setDraftFiles(files as File[]);
    } else {
      setDraftFiles([]);
    }
  }, [watchAttachments]);

  const removeDraftFile = (index: number) => {
    const updated = draftFiles.filter((_, i) => i !== index);
    setDraftFiles(updated);
    setValue("attachments", updated);
  };

  const onSubmitComment = async (data: any) => {
    if (!single?.id) return;
    const formData = new FormData();
    formData.append("comment", data.comment);
    formData.append("actionId", single.id);
    if (draftFiles.length > 0) {
      draftFiles.forEach((f) => formData.append("attachments", f, f.name));
    }
    try {
      await saveComment({
        ...DEFAULT_API_PAYLOAD,
        body: formData,
      } as PostApiV1ActionsCommentApiArg);
      toast.success("Comment added");
      reset(CommentSchema);
      setDraftFiles([]);
      setTriggerRender((p) => p + 1);
      onRefresh?.();
    } catch (error) {
      handleError(error);
    }
  };

  const triggerUpdateStatus = async (statusId: string) => {
    if (!single?.id) return;
    try {
      await updateActionStatus({
        ...DEFAULT_API_PAYLOAD,
        id: single.id,
        updateActionStatusRequest: { statusId },
      });

      const targetStatusName = (single?.dropdownStatuses || []).find(
        (s: any) => s.id === statusId,
      )?.name;
      if (
        (single?.type === "Mitigation" ||
          (single as any)?.category === "Mitigation") &&
        targetStatusName === "Closed"
      ) {
        toast.success(
          "Mitigation completed! Please navigate to the Risk Register to update the Residual Risk score for this hazard.",
          {
            duration: 6000,
          },
        );
      } else {
        toast.success("Status updated");
      }

      setTriggerRender((p) => p + 1);
      dispatch(commonActions.setTriggerReload());
      onRefresh?.();
    } catch (error) {
      handleError(error);
    }
  };

  if (!action) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={
          isCapa
            ? "max-w-5xl w-full h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden !rounded-2xl md:left-[calc(50%+8rem)] [&>div]:flex [&>div]:flex-col [&>div]:h-full [&>div]:!max-h-none [&>div]:!overflow-hidden [&>div]:!space-y-0"
            : "max-w-3xl w-full max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden md:left-[calc(50%+8rem)]"
        }
      >
        {isCapa ? (
          <>
            {/* CAPA Top Header Banner in Lime Green */}
            <div className="bg-[#CBFF10] text-neutral-900 px-6 py-2 flex flex-col gap-0.5 border-b border-neutral-200 shrink-0 rounded-t-2xl">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-bold tracking-wider uppercase opacity-85 flex items-center gap-1">
                  <Icon name="ArrowUpRight" className="h-3.5 w-3.5 shrink-0" />
                  {single?.modelType} / {single?.model?.reference}
                </span>
                {(single?.model as any)?.status && (
                  <TableBadge status={(single?.model as any).status} />
                )}
              </div>
              <h3 className="text-base font-bold text-neutral-900 mt-0.5 leading-tight">
                {single?.model?.title || "—"}
              </h3>
            </div>

            {/* CAPA Action Info & Description Block */}
            <div className="bg-white px-6 py-2 flex flex-col gap-2 shrink-0">
              {/* Row 1 Grid: Title on Left, Assignee and Status on Right */}
              <div className="grid grid-cols-12 gap-6 text-xs items-start">
                {/* Left: Title & Reference */}
                <div className="col-span-7 min-w-0">
                  <p className="text-xs text-neutral-400 font-mono">
                    {single?.reference}
                  </p>
                  <h2 className="text-lg font-bold text-[#0A2540] mt-0 leading-snug">
                    {single?.title}
                  </h2>
                </div>

                {/* Middle: Assignee */}
                <div className="col-span-3 min-w-0">
                  <p className="text-[10px] text-neutral-400 font-medium uppercase tracking-wider">
                    Assignee
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[9px] font-semibold text-neutral-700">
                      {getInitials(single?.responsiblePerson?.name ?? "?")}
                    </div>
                    <span className="font-semibold text-neutral-800 truncate text-[11px]">
                      {single?.responsiblePerson?.name || "Unassigned"}
                    </span>
                  </div>
                </div>

                {/* Right: Status */}
                {single?.status?.name && (
                  <div className="col-span-2 flex flex-col items-end min-w-0">
                    <p className="text-[10px] text-neutral-400 font-medium uppercase tracking-wider mb-1">
                      Status
                    </p>
                    {actionStatuses && actionStatuses.length > 0 ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="shrink-0 focus:outline-none">
                            <TableBadge status={single.status.name} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          side="bottom"
                          className="rounded-xl"
                        >
                          {actionStatuses?.map((item, idx) => (
                            <DropdownMenuItem
                              key={idx}
                              onClick={() => triggerUpdateStatus(item.value)}
                              className="cursor-pointer text-sm"
                            >
                              {item.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <TableBadge status={single.status.name} />
                    )}
                  </div>
                )}
              </div>

              {/* Row 2 Grid: Description on Left, Due Date on Right */}
              <div className="grid grid-cols-12 gap-6 text-xs border-b border-neutral-100 pb-2 pt-1 items-start">
                {/* Left: Description */}
                <div className="col-span-7 min-w-0">
                  <p className="text-[10px] text-neutral-400 font-semibold uppercase tracking-wider mb-1">
                    Description
                  </p>
                  <div className="text-xs text-neutral-700 leading-normal max-h-[50px] overflow-y-auto pr-1">
                    {single?.description ? (
                      <TheAduseiEditorViewer content={single.description} />
                    ) : (
                      <span className="text-neutral-400 italic">
                        No description provided.
                      </span>
                    )}
                  </div>
                </div>

                {/* Middle: Due Date */}
                <div className="col-span-3 min-w-0">
                  <p className="text-[10px] text-neutral-400 font-medium uppercase tracking-wider">
                    Due Date
                  </p>
                  <p className="font-semibold text-neutral-800 text-[11px] mt-0.5">
                    {single?.dueDate
                      ? format(new Date(single.dueDate), "MMMM dd, yyyy")
                      : "—"}
                  </p>
                </div>

                {/* Right Spacer */}
                <div className="col-span-2" />
              </div>
            </div>

            {/* Split Grid */}
            <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 px-6 pb-4 pt-2 gap-6 bg-[#F4F7FB]">
              {/* Left Column: Comments Feed with Internal Scroll */}
              <div className="flex-grow flex flex-col gap-2 min-w-0 overflow-hidden">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 shrink-0">
                  Comments
                </p>

                <div className="flex-1 overflow-y-auto pr-3 space-y-4 scrollbar-thin">
                  {isLoadingComments && (
                    <div className="flex justify-center py-12">
                      <Icon
                        name="Loader"
                        className="h-5 w-5 animate-spin text-neutral-400"
                      />
                    </div>
                  )}

                  {!isLoadingComments && comments.length === 0 && (
                    <p className="text-sm text-neutral-400 italic text-center py-12">
                      No comments yet.
                    </p>
                  )}

                  <ul className="space-y-4">
                    {comments.map((comment: any, idx: number) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E2E8F0] text-xs font-semibold text-neutral-700">
                          {getInitials(comment.createdBy?.name ?? "?")}
                        </div>
                        <div className="flex-1 min-w-0 border-b border-neutral-100 pb-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-neutral-700 truncate">
                              {comment.createdBy?.name}
                            </span>
                            <span className="text-neutral-400 text-xs">•</span>
                            <span className="text-[10px] text-neutral-400">
                              {comment.createdAt
                                ? formatCommentDate(comment.createdAt)
                                : ""}
                            </span>
                          </div>
                          <div className="mt-1.5 text-sm text-neutral-700 leading-normal">
                            <TheAduseiEditorViewer
                              content={comment.comment ?? ""}
                            />
                          </div>
                          {comment.attachments?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {comment.attachments.map(
                                (att: any, ai: number) => {
                                  const isImg = [
                                    "png",
                                    "jpg",
                                    "jpeg",
                                    "gif",
                                    "svg",
                                    "webp",
                                  ].includes(
                                    att.name?.toLowerCase().split(".").pop() ||
                                      "",
                                  );
                                  return isImg ? (
                                    <div
                                      key={ai}
                                      className="mt-1 relative rounded-xl overflow-hidden border border-neutral-200/60 max-w-[200px] shadow-xs shrink-0 bg-white p-1"
                                    >
                                      <a
                                        href={att.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block"
                                      >
                                        <img
                                          src={att.link}
                                          alt={att.name || "Attachment"}
                                          className="w-full h-auto object-cover max-h-[120px] rounded-lg hover:opacity-90 transition-opacity"
                                        />
                                      </a>
                                    </div>
                                  ) : (
                                    <div
                                      key={ai}
                                      className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white px-3 py-2 text-xs w-[200px] shrink-0 shadow-xs"
                                    >
                                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#CBFF10] text-neutral-900">
                                        <Icon
                                          name="FileText"
                                          className="h-3.5 w-3.5"
                                        />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <a
                                          href={att.link}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="truncate text-neutral-800 font-semibold hover:underline block leading-tight"
                                        >
                                          {att.name || "Attachment"}
                                        </a>
                                        <p className="text-[10px] text-neutral-400 mt-0.5">
                                          525KB • 100% uploaded
                                        </p>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Column: Composer form */}
              <div className="flex-grow flex flex-col gap-2 min-w-0 overflow-hidden">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 shrink-0">
                  Comment
                </p>

                <form
                  onSubmit={handleSubmit(onSubmitComment)}
                  className="flex-1 flex flex-col justify-between overflow-hidden gap-3"
                >
                  {/* Relative wrapper for editor and absolute top-right attach icon */}
                  <div className="relative w-full">
                    {/* Paperclip attachment button on top right */}
                    <label className="absolute top-2.5 right-2.5 z-20 cursor-pointer flex h-8 w-8 items-center justify-center rounded-lg bg-[#8E9CAE] hover:bg-[#7b8a9c] text-white transition-colors shadow-xs">
                      <Icon name="Paperclip" className="h-4 w-4" />
                      <span className="sr-only">Attach file</span>
                      <div className="hidden">
                        <FormWizard
                          config={[
                            {
                              label: "",
                              type: InputTypes.UPLOAD,
                              name: "attachments",
                              control,
                              required: false,
                              defaultValue: null,
                              errors: {
                                message: (errors as any).attachments?.message,
                                error: !!(errors as any).attachments,
                              },
                            },
                          ]}
                        />
                      </div>
                    </label>

                    {/* Rich text editor box */}
                    <FormWizard
                      config={[
                        {
                          label: "",
                          type: InputTypes.RICHTEXT,
                          name: "comment",
                          control,
                          required: false,
                          placeholder: "Add Comment",
                          className:
                            "[&_.ProseMirror]:!min-h-[120px] [&_.ProseMirror]:max-h-[160px] [&_.ProseMirror]:overflow-y-auto pr-10",
                          errors: {
                            message: (errors as any).comment?.message,
                            error: !!(errors as any).comment,
                          },
                        },
                      ]}
                    />
                  </div>

                  {/* Draft attachments preview pills outside/below the editor box */}
                  {draftFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2.5 max-h-[85px] overflow-y-auto pr-1 pb-1 shrink-0">
                      {draftFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white px-3 py-1.5 text-xs w-[200px] shrink-0 relative shadow-xs"
                        >
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#CBFF10] text-neutral-900">
                            <Icon name="FileText" className="h-3 w-3" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-neutral-800 font-semibold leading-tight">
                              {file.name}
                            </p>
                            <p className="text-[9px] text-neutral-400 mt-0.5">
                              {(file.size / 1024).toFixed(0)}KB • 100% uploaded
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDraftFile(idx)}
                            className="text-neutral-400 hover:text-red-500 transition-colors p-1"
                          >
                            <Icon name="Trash2" className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Footer bar with Save button */}
                  <div className="flex items-center justify-end pt-1 shrink-0">
                    <Button
                      type="submit"
                      disabled={postingComment}
                      className="rounded-full bg-[#0A2540] hover:bg-[#0c3154] text-white px-6 h-8 text-xs font-semibold shadow-sm transition-colors"
                    >
                      {postingComment ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* ── Header ────────────────────────────────────────────────────── */}
            <div className="bg-secondary-500/10 border-b border-secondary-200 px-6 py-4 shrink-0">
              <DialogHeader>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <ActionBadge
                      category={(single as any)?.category}
                      type={single?.type}
                    />
                    <span className="text-xs text-neutral-400 font-mono shrink-0">
                      {single?.reference}
                    </span>
                  </div>
                  {single?.status?.name && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="shrink-0">
                          <TableBadge status={single.status.name} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        side="bottom"
                        className="rounded-xl"
                      >
                        {actionStatuses?.map((item, idx) => (
                          <DropdownMenuItem
                            key={idx}
                            onClick={() => triggerUpdateStatus(item.value)}
                            className="cursor-pointer"
                          >
                            {item.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <DialogTitle className="text-lg font-semibold text-primary-400 mt-1 leading-snug">
                  {single?.title}
                </DialogTitle>
              </DialogHeader>

              {/* Meta row */}
              <div className="mt-3 flex items-center gap-6 flex-wrap text-sm">
                <div className="flex items-center gap-2">
                  <AvatarStack
                    avatarClass="h-6 w-6"
                    fallbackClass="bg-neutral-200 text-neutral-400 text-xs"
                    avatars={[
                      {
                        url: "",
                        name: getInitials(
                          single?.responsiblePerson?.name ?? "",
                        ),
                        fullname: single?.responsiblePerson?.name,
                      },
                    ]}
                  />
                  <div>
                    <p className="text-[10px] text-neutral-400 leading-none">
                      Assignee
                    </p>
                    <p className="text-sm font-medium text-neutral-700">
                      {single?.responsiblePerson?.name || "Unassigned"}
                    </p>
                  </div>
                </div>

                <div className="h-8 w-px bg-neutral-200" />

                <div>
                  <p className="text-[10px] text-neutral-400 leading-none">
                    Due Date
                  </p>
                  <p className="text-sm font-medium text-neutral-700">
                    {single?.dueDate
                      ? format(new Date(single.dueDate), "MMM dd, yyyy")
                      : "—"}
                  </p>
                </div>

                <div className="h-8 w-px bg-neutral-200" />

                <div>
                  <p className="text-[10px] text-neutral-400 leading-none">
                    Parent
                  </p>
                  <p className="text-sm font-medium text-neutral-600">
                    {single?.modelType} · {single?.model?.reference}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Body: scrollable ─────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Description */}
              {single?.description && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
                    Description
                  </p>
                  <div className="rounded-xl border border-neutral-100 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
                    <TheAduseiEditorViewer content={single.description} />
                  </div>
                </div>
              )}

              {/* Voided banner */}
              {single?.status?.name === "Voided" && (
                <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <Icon name="CircleAlert" className="h-4 w-4 shrink-0" />
                  <span>
                    This action has been voided
                    {(single as any)?.voidReason
                      ? ` — "${(single as any).voidReason}"`
                      : "."}
                  </span>
                </div>
              )}

              {/* Comments thread */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  Comments & Activity
                </p>

                {isLoadingComments && (
                  <div className="flex justify-center py-4">
                    <Icon
                      name="Loader"
                      className="h-5 w-5 animate-spin text-neutral-400"
                    />
                  </div>
                )}

                {!isLoadingComments && comments.length === 0 && (
                  <p className="text-sm text-neutral-400 italic text-center py-4">
                    No comments yet.
                  </p>
                )}

                <ul className="space-y-3">
                  {comments.map((comment: any, idx: number) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-medium text-neutral-600">
                        {getInitials(comment.createdBy?.name ?? "?")}
                      </div>
                      <div className="flex-1 rounded-xl border border-neutral-100 bg-white px-4 py-3 shadow-sm">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-semibold text-neutral-700">
                            {comment.createdBy?.name}
                          </span>
                          <span className="text-[10px] text-neutral-400">
                            {comment.createdAt
                              ? format(
                                  new Date(comment.createdAt),
                                  "MMM dd, yyyy · HH:mm",
                                )
                              : ""}
                          </span>
                        </div>
                        <TheAduseiEditorViewer
                          content={comment.comment ?? ""}
                        />
                        {comment.attachments?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {comment.attachments.map((att: any, ai: number) => (
                              <a
                                key={ai}
                                href={att.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-blue-600 hover:underline"
                              >
                                <Icon name="Paperclip" className="h-3 w-3" />
                                {att.name || "Attachment"}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* ── Footer: add comment ──────────────────────────────────────── */}
            {isNotCompleted && (
              <div className="shrink-0 border-t border-neutral-100 bg-white px-6 py-4">
                <form
                  onSubmit={handleSubmit(onSubmitComment)}
                  className="space-y-3"
                >
                  <FormWizard
                    config={[
                      {
                        label: "",
                        type: InputTypes.RICHTEXT,
                        name: "comment",
                        control,
                        required: false,
                        placeholder: isAssignedToMe
                          ? "Add a comment or describe what you did…"
                          : "Add a comment…",
                        errors: {
                          message: (errors as any).comment?.message,
                          error: !!(errors as any).comment,
                        },
                      },
                    ]}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-700">
                      <Icon name="Paperclip" className="h-3.5 w-3.5" />
                      <span>Attach file</span>
                      <FormWizard
                        config={[
                          {
                            label: "",
                            type: InputTypes.UPLOAD,
                            name: "attachments",
                            control,
                            required: false,
                            defaultValue: null,
                            errors: {
                              message: (errors as any).attachments?.message,
                              error: !!(errors as any).attachments,
                            },
                          },
                        ]}
                      />
                    </label>

                    <div className="flex items-center gap-2">
                      {/* Assignee can advance status */}
                      {isAssignedToMe &&
                        isNotCompleted &&
                        actionStatuses &&
                        actionStatuses.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
                              >
                                <Icon
                                  name="ChevronDown"
                                  className="h-3.5 w-3.5"
                                />
                                Change Status
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="rounded-xl"
                            >
                              {actionStatuses.map((item, idx) => (
                                <DropdownMenuItem
                                  key={idx}
                                  onClick={() =>
                                    triggerUpdateStatus(item.value)
                                  }
                                  className="cursor-pointer text-sm"
                                >
                                  {item.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                      <Button
                        type="submit"
                        disabled={postingComment}
                        className="flex items-center gap-1.5 rounded-lg h-8 px-4 text-xs"
                      >
                        <Icon name="Send" className="h-3.5 w-3.5" />
                        {postingComment ? "Sending…" : "Submit"}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
